// src/components/resources/glossary/GlossaryLayout.tsx

'use client'

import { type ReactNode } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import useScroll from '~/lib/use-scroll'
import { Breadcrumbs } from '~/components/resources/Breadcrumbs'
import { GlossaryNavigation } from '~/components/resources/glossary/GlossaryNavigation'
import {
  GLOSSARY_SHELL_ROOT_CLASSNAME,
  GLOSSARY_SHELL_BAR_BACKDROP_CLASSNAME,
  GLOSSARY_SHELL_BAR_BACKDROP_HEIGHT_PX,
  GLOSSARY_SHELL_NAVBAR_SPACER_CLASSNAME,
  GLOSSARY_SHELL_CONTENT_OFFSET_CLASSNAME,
} from '~/components/resources/glossary/glossary-shell'

interface GlossaryLayoutProps {
  children: ReactNode
  showNavigation?: boolean
  /**
   * Canonical label for the current-page crumb, threaded from server-fetched
   * data (e.g. a glossary term's canonical title) so the breadcrumb trail —
   * anchors and label — renders in the first-pass SSR HTML with no client
   * fetch. Omit on hub/category pages where URL-derived labels are correct.
   */
  breadcrumbLeafLabel?: string
}

export function GlossaryLayout({ children, showNavigation = true, breadcrumbLeafLabel }: GlossaryLayoutProps): JSX.Element {
  const scrolled = useScroll(50)
  const showCategoryNav = useScroll(300)

  return (
    <div className={GLOSSARY_SHELL_ROOT_CLASSNAME}>
      {/* Fixed top navigation bar that extends full width */}
      <div className="fixed top-0 left-0 right-0 z-40">
        {/* Background that extends up */}
        <div
          className={`absolute inset-0 pointer-events-none transition-all duration-300 ease-in-out ${
            scrolled
              ? 'bg-gradient-to-b from-white via-white to-white/95 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950/95'
              : GLOSSARY_SHELL_BAR_BACKDROP_CLASSNAME
          }`}
          style={{
            height: showNavigation && showCategoryNav
              ? '196px'
              : `${GLOSSARY_SHELL_BAR_BACKDROP_HEIGHT_PX}px`,
            transitionProperty: 'background-color, height, opacity'
          }}
        />

        {/* Space for Navbar */}
        <div className={GLOSSARY_SHELL_NAVBAR_SPACER_CLASSNAME} /> {/* h-20 = 80px */}
        
        {/* Breadcrumbs Container */}
        <div className="relative">
          <Breadcrumbs leafLabel={breadcrumbLeafLabel} />
        </div>

        {/* Glossary Navigation */}
        {showNavigation && (
          <AnimatePresence>
            {showCategoryNav && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="relative"
              >
                <GlossaryNavigation />
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>

      {/* Main Content - adjust padding based on navigation */}
      <div className={`${showNavigation ? 'pt-[120px]' : GLOSSARY_SHELL_CONTENT_OFFSET_CLASSNAME}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {children}
        </motion.div>
      </div>
    </div>
  )
} 
